import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useAuth } from '../context/AuthContext';
import Copilot from './Copilot';

const API = '/api';

const s = {
  layout: { display:'flex', minHeight:'100vh' },
  sidebar: { width:220, background:'rgba(15,23,42,0.98)', borderRight:'1px solid rgba(0,217,255,0.1)',
    padding:'24px 0', display:'flex', flexDirection:'column' },
  logo: { padding:'0 24px 32px', fontSize:22, fontWeight:700,
    background:'linear-gradient(135deg,#fff,#00D9FF)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' },
  navItem: (active) => ({ padding:'12px 24px', cursor:'pointer', fontSize:14, fontWeight:500,
    color: active ? '#00D9FF' : '#94a3b8',
    background: active ? 'rgba(0,217,255,0.08)' : 'transparent',
    borderLeft: active ? '3px solid #00D9FF' : '3px solid transparent' }),
  main: { flex:1, padding:32, overflowY:'auto' },
  card: { background:'rgba(15,23,42,0.8)', border:'1px solid rgba(0,217,255,0.1)',
    borderRadius:12, padding:24, marginBottom:24 },
  statRow: { display:'flex', gap:16, marginBottom:24 },
  stat: { flex:1, background:'rgba(15,23,42,0.8)', border:'1px solid rgba(0,217,255,0.1)',
    borderRadius:12, padding:20 },
  statVal: { fontSize:28, fontWeight:700, color:'#00D9FF' },
  statLabel: { fontSize:13, color:'#94a3b8', marginTop:4 },
  h2: { fontSize:18, fontWeight:600, marginBottom:16, color:'#e2e8f0' },
  table: { width:'100%', borderCollapse:'collapse' },
  th: { textAlign:'left', padding:'10px 12px', fontSize:12, color:'#64748b',
    borderBottom:'1px solid rgba(255,255,255,0.08)', textTransform:'uppercase' },
  td: { padding:'12px', fontSize:14, borderBottom:'1px solid rgba(255,255,255,0.05)' },
  btn: { padding:'8px 16px', borderRadius:8, border:'none', cursor:'pointer',
    background:'linear-gradient(135deg,#00D9FF,#0099cc)', color:'#fff', fontSize:13, fontWeight:600 },
  btnDanger: { padding:'6px 12px', borderRadius:6, border:'1px solid rgba(239,68,68,0.4)',
    cursor:'pointer', background:'transparent', color:'#fca5a5', fontSize:12 },
  input: { padding:'10px 14px', borderRadius:8, border:'1px solid rgba(255,255,255,0.15)',
    background:'rgba(255,255,255,0.05)', color:'#fff', fontSize:14, marginRight:8, outline:'none' },
  keyBox: { background:'rgba(0,217,255,0.08)', border:'1px solid rgba(0,217,255,0.3)',
    borderRadius:8, padding:'12px 16px', fontFamily:'monospace', fontSize:13,
    color:'#00D9FF', wordBreak:'break-all', marginTop:12 },
  logoutBtn: { marginTop:'auto', padding:'12px 24px', cursor:'pointer', fontSize:13,
    color:'#64748b', background:'none', border:'none', textAlign:'left' },
};

// Onboarding steps definition — order and content driven by AI action
const STEPS = [
  {
    key: 'create_api_key',
    title: 'Create your first API key',
    desc: 'Generate a key to authenticate your LLM calls through the platform.',
    action: 'API Keys',
    check: (ctx) => ctx.api_keys_count > 0,
  },
  {
    key: 'make_first_call',
    title: 'Make your first LLM call',
    desc: 'Use your API key to send a request through the Anise proxy.',
    action: null,
    check: (ctx) => ctx.total_requests > 0,
    code: (key) => `curl -X POST https://anise.zenllm.dev/v1/chat/completions \\\n  -H "Authorization: Bearer ${key}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"model":"gemini-2.0-flash","messages":[{"role":"user","content":"Hello!"}]}'`,
  },
  {
    key: 'view_usage',
    title: 'Review your usage & costs',
    desc: 'See token usage, cost breakdown, and model performance in the Usage tab.',
    action: 'Usage',
    check: (ctx) => ctx.total_requests > 0,
  },
  {
    key: 'explore_intelligence',
    title: 'Explore AI insights',
    desc: 'The platform AI analyses your usage and surfaces recommendations.',
    action: 'Intelligence',
    check: (ctx) => ctx.total_requests >= 1,
  },
];

function OnboardingChecklist({ ctx, keys, onNavigate }) {
  const activeKey = keys.find(k => !k.revoked_at);
  const completed = STEPS.filter(s => s.check(ctx)).length;
  const allDone = completed === STEPS.length;

  if (allDone) return null;

  const progress = Math.round((completed / STEPS.length) * 100);

  return (
    <div style={{ ...s.card, borderColor: 'rgba(0,217,255,0.3)', marginBottom: 24 }}>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div>
          <div style={{ fontSize:12, color:'#00D9FF', textTransform:'uppercase', letterSpacing:1, marginBottom:4 }}>
            ⬡ Getting Started
          </div>
          <div style={{ fontSize:16, fontWeight:600 }}>
            {completed}/{STEPS.length} steps complete
          </div>
        </div>
        <div style={{ fontSize:22, fontWeight:700, color:'#00D9FF' }}>{progress}%</div>
      </div>

      {/* Progress bar */}
      <div style={{ height:4, background:'rgba(255,255,255,0.08)', borderRadius:2, marginBottom:20 }}>
        <div style={{ height:'100%', width:`${progress}%`, background:'linear-gradient(90deg,#00D9FF,#0099cc)',
          borderRadius:2, transition:'width 0.4s ease' }} />
      </div>

      {/* Steps */}
      <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
        {STEPS.map((step, i) => {
          const done = step.check(ctx);
          const isNext = !done && STEPS.slice(0, i).every(s => s.check(ctx));
          return (
            <div key={step.key} style={{
              display:'flex', gap:14, alignItems:'flex-start', padding:'14px 16px',
              borderRadius:10, background: isNext ? 'rgba(0,217,255,0.06)' : 'transparent',
              border: isNext ? '1px solid rgba(0,217,255,0.2)' : '1px solid transparent',
              opacity: done ? 0.5 : 1,
            }}>
              {/* Icon */}
              <div style={{ width:24, height:24, borderRadius:'50%', flexShrink:0, marginTop:1,
                display:'flex', alignItems:'center', justifyContent:'center', fontSize:13,
                background: done ? 'rgba(0,217,255,0.2)' : isNext ? 'rgba(0,217,255,0.1)' : 'rgba(255,255,255,0.05)',
                border: done ? '1px solid #00D9FF' : '1px solid rgba(255,255,255,0.1)',
                color: done ? '#00D9FF' : '#475569',
              }}>
                {done ? '✓' : i + 1}
              </div>

              {/* Content */}
              <div style={{ flex:1 }}>
                <div style={{ fontSize:14, fontWeight:600, color: done ? '#475569' : '#e2e8f0',
                  textDecoration: done ? 'line-through' : 'none', marginBottom:3 }}>
                  {step.title}
                </div>
                <div style={{ fontSize:12, color:'#64748b' }}>{step.desc}</div>

                {/* Code snippet for make_first_call */}
                {isNext && step.code && (
                  <div style={{ marginTop:10, background:'rgba(0,0,0,0.4)', borderRadius:8,
                    padding:'10px 14px', fontFamily:'monospace', fontSize:11,
                    color:'#94a3b8', whiteSpace:'pre-wrap', wordBreak:'break-all' }}>
                    {step.code(activeKey ? '(your-api-key)' : '(create a key first)')}
                  </div>
                )}

                {/* CTA button */}
                {isNext && step.action && (
                  <button style={{ ...s.btn, marginTop:10, fontSize:12, padding:'6px 14px' }}
                    onClick={() => onNavigate(step.action)}>
                    Go to {step.action} →
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const NAV = ['Overview', 'Agents', 'Nudges', 'Cost Analysis', 'Usage', 'API Keys', 'Streaming', 'Billing', 'Admin'];

const TYPE_LABELS_MAP = {
  copilot: '⬡ Copilot',
  cost:    '💰 Cost Analyst',
  cs:      '🤝 Customer Success',
  product: '🧠 Product Intelligence',
};

// Simple pie chart component for action distribution
function ActionPie({ data }) {
  const total = data.reduce((a, b) => a + (b.count || 0), 0);
  if (total === 0) return <div style={{ color: '#64748b' }}>No data</div>;
  
  let start = 0;
  const colors = ['#00D9FF', '#00ff88', '#ff6b6b', '#ffd93d', '#a855f7', '#6366f1'];
  
  return (
    <div style={{ position: 'relative', width: 160, height: 160 }}>
      <svg viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)', width: '100%', height: '100%' }}>
        {data.map((d, i) => {
          const pct = (d.count / total) * 100;
          const end = start + (pct * 3.6);
          const largeArc = pct > 50 ? 1 : 0;
          const x1 = 50 + 40 * Math.cos((start * Math.PI) / 180);
          const y1 = 50 + 40 * Math.sin((start * Math.PI) / 180);
          const x2 = 50 + 40 * Math.cos((end * Math.PI) / 180);
          const y2 = 50 + 40 * Math.sin((end * Math.PI) / 180);
          const path = start === 0 && end >= 360 
            ? `M 50,50 m -40,0 a 40,40 0 1,0 80,0 a 40,40 0 1,0 -80,0`
            : `M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`;
          const el = <path key={i} d={path} fill={colors[i % colors.length]} />;
          start = end;
          return el;
        })}
      </svg>
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        fontSize: 24, fontWeight: 700, color: '#fff' }}>{total}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState('Overview');
  const [costs, setCosts] = useState({ total_cost_usd: 0, total_requests: 0 });
  const [usage, setUsage] = useState([]);
  const [keys, setKeys] = useState([]);
  const [models, setModels] = useState([]);
  const [newKey, setNewKey] = useState(null);
  const [keyName, setKeyName] = useState('');
  const [decisions, setDecisions] = useState([]);
  const [ctx, setCtx] = useState(null);
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [evals, setEvals] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [newPrompt, setNewPrompt] = useState({ name: 'brain_v1', content: '' });
  const [cacheStats, setCacheStats] = useState(null);
  const [billing, setBilling] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [streamConfig, setStreamConfig] = useState(null);
  const [nudges, setNudges] = useState([]);
  const [threads, setThreads] = useState([]);
  const [costAnalysis, setCostAnalysis] = useState(null);
  const [agentType, setAgentType] = useState('copilot');
  const [activeThread, setActiveThread] = useState(null);
  const [adminToken, setAdminToken] = useState(() => localStorage.getItem('anise_admin_token') || '');

  const tid = user?.tenantId;

  const adminCfg = adminToken.trim()
    ? { headers: { 'X-Admin-Token': adminToken.trim() } }
    : undefined;

  const refresh = useCallback(() => {
    if (!tid) return;
    axios.get(`${API}/tenants/${tid}/costs`).then(r => setCosts({
      total_cost_usd: parseFloat(r.data.total_cost_usd) || 0,
      total_requests: r.data.total_requests || 0,
    })).catch(() => {});
    axios.get(`${API}/tenants/${tid}/usage`).then(r => setUsage(r.data)).catch(() => {});
    axios.get(`${API}/tenants/${tid}/keys`).then(r => setKeys(r.data)).catch(() => {});
    axios.get(`${API}/models`).then(r => setModels(r.data)).catch(() => {});
    axios.get(`${API}/tenants/${tid}/decisions`).then(r => setDecisions(r.data)).catch(() => {});
    axios.get(`${API}/context/${tid}`).then(r => setCtx(r.data)).catch(() => {});
    if (adminCfg) {
      axios.get(`${API}/admin/evals`, adminCfg).then(r => setEvals(r.data)).catch(() => {});
      axios.get(`${API}/admin/prompts`, adminCfg).then(r => setPrompts(r.data)).catch(() => {});
    }
    axios.get(`${API}/tenants/${tid}/cache-stats`).then(r => setCacheStats(r.data)).catch(() => {});
    axios.get(`${API}/tenants/${tid}/billing`).then(r => setBilling(r.data)).catch(() => {});
    axios.get(`${API}/tenants/${tid}/invoices`).then(r => setInvoices(r.data)).catch(() => {});
    axios.get(`${API}/tenants/${tid}/stream`).then(r => setStreamConfig(r.data)).catch(() => {});
    axios.get(`${API}/tenants/${tid}/nudges`).then(r => setNudges(r.data)).catch(() => {});
    axios.get(`${API}/tenants/${tid}/threads`).then(r => setThreads(r.data)).catch(() => {});
    axios.get(`${API}/tenants/${tid}/cost-analysis`).then(r => setCostAnalysis(r.data)).catch(() => {});
  }, [tid, adminCfg]);

  useEffect(() => { refresh(); }, [refresh]);

  // Track dashboard view as an event for the context pipeline
  useEffect(() => {
    if (!tid) return;
    axios.post(`${API}/tenants/${tid}/events`, { event_type: 'dashboard_viewed', payload: {} }).catch(() => {});
  }, [tid]);

  const createKey = async () => {
    const res = await axios.post(`${API}/tenants/${tid}/keys?name=${keyName || 'default'}`);
    setNewKey(res.data.key);
    refresh();
    setKeyName('');
  };

  const revokeKey = async (kid) => {
    await axios.delete(`${API}/tenants/${tid}/keys/${kid}`);
    setKeys(keys.filter(k => k.id !== kid));
  };

  const chartData = usage.map(u => ({
    name: `${u.date} ${u.model}`,
    cost: parseFloat(u.total_cost_usd),
    requests: u.total_requests,
  }));

  return (
    <div style={s.layout}>
      <div style={s.sidebar}>
        <div style={s.logo}>⬡ Anise</div>
        {NAV.map(n => (
          <div key={n} style={s.navItem(tab === n)} onClick={() => setTab(n)}>{n}</div>
        ))}
        <div style={{ flex:1 }} />
        <div style={{ padding:'0 24px 8px', fontSize:12, color:'#475569' }}>{user?.email}</div>
        <button style={s.logoutBtn} onClick={logout}>Sign out</button>
      </div>

      <div style={s.main}>

        {tab === 'Overview' && (
          <>
            {/* AI-guided onboarding checklist */}
            {ctx && (
              <OnboardingChecklist
                ctx={ctx}
                keys={keys}
                onNavigate={setTab}
              />
            )}

            <div style={s.statRow}>
              <div style={s.stat}>
                <div style={s.statVal}>${costs.total_cost_usd.toFixed(4)}</div>
                <div style={s.statLabel}>Total Cost (USD)</div>
              </div>
              <div style={s.stat}>
                <div style={s.statVal}>{costs.total_requests}</div>
                <div style={s.statLabel}>Total Requests</div>
              </div>
              <div style={s.stat}>
                <div style={s.statVal}>{usage.length}</div>
                <div style={s.statLabel}>Active Models</div>
              </div>
              {cacheStats && (
                <div style={s.stat}>
                  <div style={{ ...s.statVal, color: '#00ff88' }}>
                    {(cacheStats.hit_rate * 100).toFixed(0)}%
                  </div>
                  <div style={s.statLabel}>Cache Hit Rate</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                    ${cacheStats.total_saved_usd.toFixed(4)} saved
                  </div>
                </div>
              )}
            </div>

            <div style={s.card}>
              <div style={s.h2}>Cost by Model (last 7 days)</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="name" tick={{ fill:'#64748b', fontSize:11 }} />
                  <YAxis tick={{ fill:'#64748b', fontSize:11 }} />
                  <Tooltip contentStyle={{ background:'#0f172a', border:'1px solid #00D9FF', borderRadius:8 }} />
                  <Bar dataKey="cost" fill="#00D9FF" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}

        {tab === 'Usage' && (
          <div style={s.card}>
            <div style={s.h2}>Usage (last 7 days)</div>
            <table style={s.table}>
              <thead>
                <tr>
                  {['Date','Model','Requests','Tokens','Cost (USD)'].map(h => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {usage.map((u, i) => (
                  <tr key={i}>
                    <td style={s.td}>{u.date}</td>
                    <td style={s.td}>{u.model}</td>
                    <td style={s.td}>{u.total_requests}</td>
                    <td style={s.td}>{u.total_tokens?.toLocaleString()}</td>
                    <td style={s.td}>${parseFloat(u.total_cost_usd).toFixed(6)}</td>
                  </tr>
                ))}
                {usage.length === 0 && (
                  <tr><td colSpan={5} style={{ ...s.td, color:'#475569', textAlign:'center' }}>No usage yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'API Keys' && (
          <div style={s.card}>
            <div style={s.h2}>API Keys</div>
            <div style={{ marginBottom:20 }}>
              <input style={s.input} placeholder="Key name (optional)"
                value={keyName} onChange={e => setKeyName(e.target.value)} />
              <button style={s.btn} onClick={createKey}>Create Key</button>
            </div>
            {newKey && (
              <div style={s.keyBox}>
                <div style={{ fontSize:11, color:'#94a3b8', marginBottom:4 }}>
                  ⚠ Copy this key now — it won't be shown again
                </div>
                {newKey}
              </div>
            )}
            <table style={{ ...s.table, marginTop:20 }}>
              <thead>
                <tr>
                  {['Name','Created','Status',''].map(h => <th key={h} style={s.th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {keys.map(k => (
                  <tr key={k.id}>
                    <td style={s.td}>{k.name}</td>
                    <td style={s.td}>{new Date(k.created_at).toLocaleDateString()}</td>
                    <td style={s.td}>{k.revoked_at ? '🔴 Revoked' : '🟢 Active'}</td>
                    <td style={s.td}>
                      {!k.revoked_at && (
                        <button style={s.btnDanger} onClick={() => revokeKey(k.id)}>Revoke</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'Intelligence' && (
          <>
            {/* Eval Metrics Card */}
            {evals && (
              <div style={s.card}>
                <div style={{ ...s.h2, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <span>AI Decision Accuracy (7 days)</span>
                  {evals.accuracy != null && (
                    <span style={{ fontSize: 28, fontWeight: 700, color: evals.accuracy >= 0.7 ? '#00ff88' : '#ffd93d' }}>
                      {(evals.accuracy * 100).toFixed(0)}%
                    </span>
                  )}
                </div>
                <div style={{ display:'flex', gap: 24, alignItems:'center', flexWrap:'wrap' }}>
                  <ActionPie data={evals.by_action || []} />
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12, textTransform:'uppercase', letterSpacing: 1 }}>
                      Action Distribution
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap: 8 }}>
                      {(evals.by_action || []).map((a, i) => {
                        const colors = ['#00D9FF', '#00ff88', '#ff6b6b', '#ffd93d', '#a855f7', '#6366f1'];
                        return (
                          <div key={i} style={{ display:'flex', alignItems:'center', gap: 8 }}>
                            <div style={{ width: 10, height: 10, borderRadius: '50%', background: colors[i % colors.length] }} />
                            <span style={{ fontSize: 13, color: '#e2e8f0', flex: 1 }}>{a.action}</span>
                            <span style={{ fontSize: 13, color: '#64748b' }}>{a.count}</span>
                            <span style={{ fontSize: 12, color: '#94a3b8' }}>({((a.avg_confidence || 0) * 100).toFixed(0)}% conf)</span>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                      <div style={{ fontSize: 12, color: '#64748b' }}>Total Decisions</div>
                      <div style={{ fontSize: 20, fontWeight: 600, color: '#fff' }}>{evals.total_decisions || 0}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Decision Log */}
            <div style={s.card}>
              <div style={s.h2}>AI Decision Log</div>
              <div style={{ fontSize:13, color:'#94a3b8', marginBottom:16 }}>
                Every decision the platform AI has made for your account. 
                <span style={{ color: '#00ff88' }}>Green</span> = led to expected outcome, 
                <span style={{ color: '#ff6b6b' }}>Red</span> = missed.
              </div>
              <table style={s.table}>
                <thead>
                  <tr>{['Action','Reason','Confidence','Outcome','Time'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {decisions.length === 0 && (
                    <tr><td colSpan={5} style={{ ...s.td, color:'#475569', textAlign:'center' }}>
                      No decisions yet — the AI will analyse your account as you use the platform
                    </td></tr>
                  )}
                  {decisions.map((d, i) => (
                    <tr key={i}>
                      <td style={s.td}><span style={{ color:'#00D9FF' }}>{d.action}</span></td>
                      <td style={s.td}>{d.reason}</td>
                      <td style={s.td}>{d.confidence != null ? `${(parseFloat(d.confidence)*100).toFixed(0)}%` : '—'}</td>
                      <td style={s.td}>
                        {d.outcome === 'success' && <span style={{ color: '#00ff88' }}>✓ Success</span>}
                        {d.outcome === 'missed' && <span style={{ color: '#ff6b6b' }}>✗ Missed</span>}
                        {!d.outcome && <span style={{ color: '#64748b' }}>Pending</span>}
                      </td>
                      <td style={s.td}>{new Date(d.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === 'Models' && (
          <div style={s.card}>
            <div style={s.h2}>Available Models</div>
            <table style={s.table}>
              <thead>
                <tr>
                  {['Model ID','Provider','Input $/1k tokens','Output $/1k tokens'].map(h => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {models.map(m => (
                  <tr key={m.id}>
                    <td style={s.td}><code style={{ color:'#00D9FF' }}>{m.id}</code></td>
                    <td style={s.td}>{m.provider}</td>
                    <td style={s.td}>${m.input_cost_per_1k}</td>
                    <td style={s.td}>${m.output_cost_per_1k}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'Streaming' && (
          <>
            <div style={s.card}>
              <div style={s.h2}>Event Streaming</div>
              <div style={{ fontSize:13, color:'#94a3b8', marginBottom:20 }}>
                Stream behavioural events (click, skip, abandon) from your product directly into Anise
                using your existing API key. Powered by Redpanda.
              </div>

              {streamConfig?.provisioned ? (
                <>
                  <div style={{ display:'flex', gap:16, marginBottom:24, flexWrap:'wrap' }}>
                    <div style={s.stat}>
                      <div style={{ ...s.statVal, color:'#00ff88', fontSize:16 }}>● Active</div>
                      <div style={s.statLabel}>Stream Status</div>
                    </div>
                    <div style={s.stat}>
                      <div style={{ ...s.statVal, fontSize:14, fontFamily:'monospace' }}>
                        {streamConfig.broker_type}
                      </div>
                      <div style={s.statLabel}>Broker</div>
                    </div>
                    <div style={s.stat}>
                      <div style={{ ...s.statVal, fontSize:13, fontFamily:'monospace' }}>
                        {streamConfig.topic}
                      </div>
                      <div style={s.statLabel}>Topic</div>
                    </div>
                  </div>

                  <div style={{ marginBottom:20 }}>
                    <div style={{ fontSize:13, fontWeight:600, marginBottom:8 }}>Endpoint</div>
                    <div style={{ ...s.keyBox, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <span>{streamConfig.endpoint}</span>
                      <button style={{ ...s.btn, padding:'4px 12px', fontSize:11, marginLeft:12, flexShrink:0 }}
                        onClick={() => navigator.clipboard.writeText(streamConfig.endpoint)}>
                        Copy
                      </button>
                    </div>
                  </div>

                  <div style={{ marginBottom:8, fontSize:13, fontWeight:600 }}>Example request</div>
                  <div style={{ background:'rgba(0,0,0,0.4)', borderRadius:8, padding:'14px 16px',
                    fontFamily:'monospace', fontSize:12, color:'#94a3b8', whiteSpace:'pre-wrap' }}>
{`curl -X POST ${streamConfig.endpoint} \\
  -H "Authorization: Bearer <your-api-key>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "events": [
      { "type": "click",   "element": "buy_button", "ts": 1700000000 },
      { "type": "skip",    "element": "promo_banner", "ts": 1700000001 },
      { "type": "abandon", "element": "checkout",    "ts": 1700000002 }
    ]
  }'`}
                  </div>
                </>
              ) : (
                <div style={{ textAlign:'center', padding:'40px 0' }}>
                  <div style={{ fontSize:40, marginBottom:16 }}>📡</div>
                  <div style={{ fontSize:15, color:'#94a3b8', marginBottom:8 }}>
                    Streaming is not yet provisioned for your account.
                  </div>
                  <div style={{ fontSize:13, color:'#64748b' }}>
                    Contact your admin to enable streaming, or run:
                  </div>
                  <div style={{ marginTop:16, background:'rgba(0,0,0,0.4)', borderRadius:8,
                    padding:'12px 16px', fontFamily:'monospace', fontSize:12,
                    color:'#94a3b8', display:'inline-block', textAlign:'left' }}>
                    {`curl -X POST https://anise.zenllm.dev/admin/stream/provision \\
  -H "X-Admin-Token: <admin-token>" \\
  -H "Content-Type: application/json" \\
  -d '{"tenant_id": "${tid}"}'`}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {tab === 'Nudges' && (
          <>
            <div style={{ ...s.h2, marginBottom: 8 }}>AI Nudges</div>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>
              Actions your AI agents have surfaced. Each nudge replaces a manual CS or product team check-in.
            </div>
            {nudges.length === 0 && (
              <div style={{ ...s.card, textAlign: 'center', color: '#475569', padding: 40 }}>
                No active nudges — your AI agents are watching and will surface insights when thresholds are crossed.
              </div>
            )}
            {nudges.map(n => {
              const colors = { high: '#ff6b6b', medium: '#ffd93d', low: '#00ff88' };
              const icons  = { churn_risk_alert: '🚨', show_upgrade_nudge: '⬆', suggest_cost_saving: '💰',
                               feature_adoption_nudge: '💡', onboarding_next_step: '🚀', high_value_alert: '⭐', no_action: '✓' };
              return (
                <div key={n.id} style={{ ...s.card, borderColor: `${colors[n.priority] || '#00D9FF'}33`,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                      <span style={{ fontSize: 20 }}>{icons[n.action] || '⬡'}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: colors[n.priority] || '#00D9FF',
                        textTransform: 'uppercase', letterSpacing: 0.5 }}>{n.action.replace(/_/g, ' ')}</span>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4,
                        background: `${colors[n.priority] || '#00D9FF'}22`, color: colors[n.priority] || '#00D9FF' }}>
                        {n.priority}
                      </span>
                    </div>
                    <div style={{ fontSize: 14, color: '#e2e8f0', marginBottom: 6 }}>{n.message}</div>
                    <div style={{ fontSize: 11, color: '#475569' }}>{new Date(n.created_at).toLocaleString()}</div>
                  </div>
                  <button style={{ ...s.btn, fontSize: 12, padding: '6px 14px', flexShrink: 0,
                    background: 'rgba(0,255,136,0.15)', color: '#00ff88', border: '1px solid rgba(0,255,136,0.3)' }}
                    onClick={async () => {
                      await axios.post(`${API}/tenants/${tid}/nudges/${n.id}/resolve`);
                      refresh();
                    }}>Mark resolved ✓</button>
                </div>
              );
            })}
          </>
        )}

        {tab === 'Agents' && (
          <>
            <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
              {[['copilot','⬡ Copilot','#00D9FF'],['cost','💰 Cost Analyst','#ffd93d'],
                ['cs','🤝 Customer Success','#00ff88'],['product','🧠 Product','#a855f7']].map(([type, label, color]) => (
                <button key={type} onClick={() => { setAgentType(type); setActiveThread(null); }}
                  style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${agentType===type ? color : 'rgba(255,255,255,0.1)'}`,
                    background: agentType===type ? `${color}18` : 'transparent',
                    color: agentType===type ? color : '#64748b', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                  {label}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 16 }}>
              {/* Thread list */}
              <div style={{ width: 220, flexShrink: 0 }}>
                <button style={{ ...s.btn, width: '100%', marginBottom: 12, fontSize: 12 }}
                  onClick={() => { setActiveThread(null); setCopilotOpen(true); }}>
                  + New conversation
                </button>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {threads.filter(t => t.thread_type === agentType).map(t => (
                    <div key={t.id}
                      onClick={() => { setActiveThread(t); setCopilotOpen(true); }}
                      style={{ padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                        background: activeThread?.id === t.id ? 'rgba(0,217,255,0.08)' : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${activeThread?.id === t.id ? 'rgba(0,217,255,0.3)' : 'rgba(255,255,255,0.06)'}` }}>
                      <div style={{ fontSize: 13, color: '#e2e8f0', marginBottom: 3,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</div>
                      <div style={{ fontSize: 11, color: '#475569' }}>{new Date(t.last_active_at).toLocaleDateString()}</div>
                    </div>
                  ))}
                  {threads.filter(t => t.thread_type === agentType).length === 0 && (
                    <div style={{ fontSize: 12, color: '#334155', padding: '8px 4px' }}>No conversations yet</div>
                  )}
                </div>
              </div>
              {/* Agent info panel */}
              <div style={{ flex: 1 }}>
                {[{type:'copilot',color:'#00D9FF',desc:'Your general AI assistant. Knows your full account context — costs, usage, events, and behavioural signals. Ask anything.',role:'Replaces: ad-hoc data queries to your engineering or analytics team'},
                  {type:'cost',color:'#ffd93d',desc:'Analyses your LLM spend by feature, finds waste, projects future costs, and recommends optimisations.',role:'Replaces: monthly cost review meetings with your finance team'},
                  {type:'cs',color:'#00ff88',desc:'Monitors churn signals, support volume, and booking conversion. Surfaces at-risk accounts and recommends interventions.',role:'Replaces: weekly customer success team account reviews'},
                  {type:'product',color:'#a855f7',desc:'Analyses user behaviour, feature adoption, and drop-off points. Recommends product changes backed by real data.',role:'Replaces: weekly product team meetings and user research synthesis'},
                ].filter(a => a.type === agentType).map(a => (
                  <div key={a.type} style={s.card}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: a.color, marginBottom: 8 }}>
                      {TYPE_LABELS_MAP[a.type]}
                    </div>
                    <div style={{ fontSize: 14, color: '#94a3b8', marginBottom: 12, lineHeight: 1.6 }}>{a.desc}</div>
                    <div style={{ fontSize: 12, padding: '8px 12px', borderRadius: 6,
                      background: `${a.color}11`, color: a.color, border: `1px solid ${a.color}33` }}>
                      {a.role}
                    </div>
                    <button style={{ ...s.btn, marginTop: 16 }}
                      onClick={() => { setActiveThread(null); setCopilotOpen(true); }}>
                      Start conversation →
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {tab === 'Cost Analysis' && (
          <>
            <div style={{ ...s.h2, marginBottom: 20 }}>Cost Analysis</div>
            {costAnalysis ? (
              <>
                <div style={s.statRow}>
                  <div style={s.stat}>
                    <div style={s.statVal}>${(costAnalysis.total_cost_usd||0).toFixed(4)}</div>
                    <div style={s.statLabel}>Total (7 days)</div>
                  </div>
                  <div style={s.stat}>
                    <div style={{ ...s.statVal, color: '#ffd93d' }}>${(costAnalysis.projected_monthly_usd||0).toFixed(2)}</div>
                    <div style={s.statLabel}>Projected Monthly</div>
                  </div>
                  <div style={s.stat}>
                    <div style={s.statVal}>{costAnalysis.efficiency?.avg_tokens_per_request || 0}</div>
                    <div style={s.statLabel}>Avg Tokens / Request</div>
                  </div>
                  <div style={s.stat}>
                    <div style={s.statVal}>{costAnalysis.efficiency?.avg_latency_ms || 0}ms</div>
                    <div style={s.statLabel}>Avg Latency</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  <div style={{ ...s.card, flex: 1, minWidth: 280 }}>
                    <div style={s.h2}>Cost by Feature</div>
                    {(costAnalysis.by_feature || []).map((f, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between',
                        padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <span style={{ fontSize: 14, color: '#e2e8f0' }}>{f.feature}</span>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: '#00D9FF' }}>${parseFloat(f.cost_usd||0).toFixed(6)}</div>
                          <div style={{ fontSize: 11, color: '#64748b' }}>{f.requests} req · {(f.tokens||0).toLocaleString()} tokens</div>
                        </div>
                      </div>
                    ))}
                    {(costAnalysis.by_feature||[]).length === 0 && <div style={{ color: '#475569', fontSize: 13 }}>No data yet</div>}
                  </div>
                  <div style={{ ...s.card, flex: 1, minWidth: 280 }}>
                    <div style={s.h2}>Daily Trend</div>
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={(costAnalysis.daily_trend||[]).map(d => ({ name: d.date, cost: parseFloat(d.cost_usd||0) }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                        <XAxis dataKey="name" tick={{ fill:'#64748b', fontSize:10 }} />
                        <YAxis tick={{ fill:'#64748b', fontSize:10 }} />
                        <Tooltip contentStyle={{ background:'#0f172a', border:'1px solid #ffd93d', borderRadius:8 }} />
                        <Bar dataKey="cost" fill="#ffd93d" radius={[4,4,0,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </>
            ) : <div style={{ color: '#475569' }}>Loading cost analysis...</div>}
          </>
        )}

        {tab === 'Billing' && (
          <>
            {/* Current Plan */}
            <div style={s.card}>
              <div style={s.h2}>Current Plan</div>
              {billing ? (
                <div style={{ display:'flex', gap:24, alignItems:'center', flexWrap:'wrap' }}>
                  <div style={s.stat}>
                    <div style={{ ...s.statVal, color: billing.plan === 'free' ? '#94a3b8' : '#00ff88' }}>
                      {billing.plan_name || billing.plan}
                    </div>
                    <div style={s.statLabel}>Active Plan</div>
                  </div>
                  <div style={s.stat}>
                    <div style={s.statVal}>
                      {billing.token_limit ? billing.token_limit.toLocaleString() : '∞'}
                    </div>
                    <div style={s.statLabel}>Monthly Token Limit</div>
                  </div>
                  {billing.stripe_customer_id && (
                    <button style={s.btn} onClick={async () => {
                      try {
                        const r = await axios.get(`${API}/tenants/${tid}/billing/portal`);
                        window.open(r.data.url, '_blank');
                      } catch { alert('Could not open billing portal'); }
                    }}>
                      Manage Billing ↗
                    </button>
                  )}
                </div>
              ) : (
                <div style={{ color:'#64748b' }}>Loading...</div>
              )}
            </div>

            {/* Upgrade */}
            {billing?.plan === 'free' && (
              <div style={s.card}>
                <div style={s.h2}>Upgrade Your Plan</div>
                <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
                  {[{plan:'starter',label:'Starter',price:'$49/mo',tokens:'5M tokens'},
                    {plan:'pro',label:'Pro',price:'$199/mo',tokens:'50M tokens'},
                    {plan:'enterprise',label:'Enterprise',price:'Custom',tokens:'Unlimited'}].map(p => (
                    <div key={p.plan} style={{ ...s.stat, flex:'0 0 200px', cursor:'pointer',
                      border:'1px solid rgba(0,217,255,0.2)' }}>
                      <div style={{ fontSize:16, fontWeight:700, color:'#e2e8f0', marginBottom:4 }}>{p.label}</div>
                      <div style={{ fontSize:22, fontWeight:700, color:'#00D9FF', marginBottom:4 }}>{p.price}</div>
                      <div style={{ fontSize:12, color:'#64748b', marginBottom:16 }}>{p.tokens}/month</div>
                      <button style={{ ...s.btn, width:'100%', fontSize:12 }} onClick={async () => {
                        const email = prompt('Billing email:');
                        if (!email) return;
                        try {
                          await axios.post(`${API}/tenants/${tid}/billing/upgrade`,
                            { plan: p.plan, billing_email: email });
                          refresh();
                          alert(`Upgraded to ${p.label}!`);
                        } catch(e) { alert('Upgrade failed: ' + (e.response?.data?.detail || e.message)); }
                      }}>Upgrade →</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Invoice History */}
            <div style={s.card}>
              <div style={s.h2}>Invoice History</div>
              <table style={s.table}>
                <thead>
                  <tr>{['Period','Amount Due','Amount Paid','Status','Date'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {invoices.length === 0 && (
                    <tr><td colSpan={5} style={{ ...s.td, color:'#475569', textAlign:'center' }}>No invoices yet</td></tr>
                  )}
                  {invoices.map((inv, i) => (
                    <tr key={i}>
                      <td style={s.td}>
                        {inv.period_start ? new Date(inv.period_start).toLocaleDateString() : '—'}
                        {' → '}
                        {inv.period_end ? new Date(inv.period_end).toLocaleDateString() : '—'}
                      </td>
                      <td style={s.td}>${parseFloat(inv.amount_due || 0).toFixed(2)}</td>
                      <td style={s.td}>${parseFloat(inv.amount_paid || 0).toFixed(2)}</td>
                      <td style={s.td}>
                        <span style={{ color: inv.status === 'paid' ? '#00ff88' : '#ffd93d',
                          fontSize:12, padding:'2px 8px',
                          background: inv.status === 'paid' ? 'rgba(0,255,136,0.1)' : 'rgba(255,217,61,0.1)',
                          borderRadius:4 }}>
                          {inv.status}
                        </span>
                      </td>
                      <td style={s.td}>{new Date(inv.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === 'Admin' && (
          <div style={s.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <div style={s.h2}>Prompt Management (Admin)</div>
                <div style={{ fontSize:13, color:'#94a3b8' }}>
                  Versioned system prompts for the AI brain. Update without redeploying.
                </div>
              </div>
              <button
                style={{ ...s.btn, background: 'linear-gradient(135deg,#f97316,#ea580c)',
                  boxShadow: '0 4px 16px rgba(249,115,22,0.3)', padding: '10px 20px', fontSize: 13 }}
                onClick={() => {
                  setAgentType('admin');
                  setActiveThread(null);
                  setCopilotOpen(true);
                }}
              >
                🔧 Status Check
              </button>
            </div>

            {/* Admin token */}
            <div style={{ marginBottom: 16, padding: 14, background: 'rgba(0,0,0,0.2)', borderRadius: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Admin token</div>
              <div style={{ display:'flex', gap: 10, alignItems:'center', flexWrap:'wrap' }}>
                <input
                  style={{ ...s.input, width: 420, maxWidth: '100%' }}
                  placeholder="Paste ADMIN_TOKEN (stored locally in this browser)"
                  value={adminToken}
                  onChange={e => {
                    const v = e.target.value;
                    setAdminToken(v);
                    localStorage.setItem('anise_admin_token', v);
                  }}
                />
                <button
                  style={{ ...s.btn, padding:'8px 14px', fontSize:12, opacity: adminToken.trim() ? 1 : 0.6 }}
                  disabled={!adminToken.trim()}
                  onClick={() => refresh()}
                >
                  Load admin data
                </button>
              </div>
              <div style={{ fontSize:12, color:'#64748b', marginTop:8 }}>
                Tip: from your terminal: <code style={{ color:'#94a3b8' }}>kubectl get secret anise-secrets -n anise -o jsonpath='&#123;.data.ADMIN_TOKEN&#125;' | base64 -d</code>
              </div>
            </div>

            {/* New Prompt Form */}
            <div style={{ marginBottom: 24, padding: 16, background: 'rgba(0,0,0,0.2)', borderRadius: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Create New Version</div>
              <input
                style={{ ...s.input, width: 200, marginBottom: 12 }}
                placeholder="Prompt name"
                value={newPrompt.name}
                onChange={e => setNewPrompt({ ...newPrompt, name: e.target.value })}
              />
              <textarea
                style={{ ...s.input, width: '100%', height: 120, marginBottom: 12, fontFamily: 'monospace', fontSize: 12 }}
                placeholder="System prompt content..."
                value={newPrompt.content}
                onChange={e => setNewPrompt({ ...newPrompt, content: e.target.value })}
              />
              <button
                style={{ ...s.btn, opacity: (newPrompt.content.trim() && adminCfg) ? 1 : 0.5 }}
                disabled={!newPrompt.content.trim() || !adminCfg}
                onClick={async () => {
                  try {
                    await axios.post(`${API}/admin/prompts`, newPrompt, adminCfg);
                    setNewPrompt({ name: 'brain_v1', content: '' });
                    refresh();
                  } catch (e) {
                    alert('Failed to create prompt: ' + (e.response?.data?.detail || e.message));
                  }
                }}
              >
                Create Version
              </button>
            </div>

            {/* Prompt History Table */}
            <table style={s.table}>
              <thead>
                <tr>{['Name','Version','Active','Created',''].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {prompts.length === 0 && (
                  <tr><td colSpan={5} style={{ ...s.td, color:'#475569', textAlign:'center' }}>
                    No prompts yet. Create the first brain_v1 prompt to enable AI decisions.
                  </td></tr>
                )}
                {prompts.map(p => (
                  <tr key={p.id}>
                    <td style={s.td}><code style={{ color:'#00D9FF' }}>{p.name}</code></td>
                    <td style={s.td}>v{p.version}</td>
                    <td style={s.td}>
                      {p.active ? (
                        <span style={{ color: '#00ff88', fontSize: 12, padding: '2px 8px', background: 'rgba(0,255,136,0.1)', borderRadius: 4 }}>
                          ● Active
                        </span>
                      ) : (
                        <span style={{ color: '#64748b', fontSize: 12 }}>—</span>
                      )}
                    </td>
                    <td style={s.td}>{new Date(p.created_at).toLocaleString()}</td>
                    <td style={s.td}>
                      {!p.active && (
                        <button style={{ ...s.btn, padding: '4px 12px', fontSize: 12 }}
                          onClick={async () => {
                            // Re-create this prompt to make it the latest active version
                            try {
                              await axios.post(`${API}/admin/prompts`, { name: p.name, content: p.content }, adminCfg);
                              refresh();
                            } catch (e) {
                              alert('Failed: ' + (e.response?.data?.detail || e.message));
                            }
                          }}>
                          Rollback to this
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </div>

      {/* Copilot floating button */}
      <button data-testid="copilot-btn" onClick={() => setCopilotOpen(o => !o)} style={{
        position: 'fixed', bottom: 28, right: copilotOpen ? 436 : 28,
        width: 52, height: 52, borderRadius: '50%', border: 'none', cursor: 'pointer',
        background: 'linear-gradient(135deg,#00D9FF,#0099cc)',
        color: '#0A1628', fontSize: 22, fontWeight: 700,
        boxShadow: '0 4px 20px rgba(0,217,255,0.4)',
        transition: 'right 0.2s ease', zIndex: 101,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {copilotOpen ? '×' : '⬡'}
      </button>

      {copilotOpen && (
        <Copilot
          tenantId={tid}
          threadType={agentType}
          threadId={activeThread?.id || threads.find(t => t.thread_type === agentType)?.id}
          threadTitle={activeThread?.title || threads.find(t => t.thread_type === agentType)?.title}
          onClose={() => setCopilotOpen(false)}
          onNewThread={(newId, title) => {
            const newThread = { id: newId, thread_type: agentType, title, last_active_at: new Date().toISOString() };
            setThreads(prev => [newThread, ...prev]);
            setActiveThread(newThread);
          }}
        />
      )}
    </div>
  );
}
